import { Formik } from 'formik';
import * as Yup from 'yup';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getActiveBranchesByCompany, setRevenueTarget } from '../../../../../api/requests';
import Button from '../../../../../components/form-control/Button';
import SelectDropdown from '../../../../../components/form-control/SelectDropdown';
import Input from '../../../../../components/form-control/Input';
import useUserAccount from '../../../../../hooks/userAccountHook';
import DatePickerInput from '../../../../../components/form-control/DatePickerInput';
import { showToast } from '../../../../../utils/miscFunctions';


interface IRNPLProps {
    onClose: () => void;
    onProceed: () => void;
}

export interface ICreateRevenueTargetDTO {
    branchId: number,
    month: string,
    targetAmount: number,
    year: number
}


const CreateRevenueTarget = (props: IRNPLProps): JSX.Element => {
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [branches, setBranches] = useState<any>([]);
    const { user } = useUserAccount();
    const companyId = Number(user?.company?.id);

    const getBranchesByCompany = async () => {
        try {
            const res: any = await getActiveBranchesByCompany(Number(user?.company?.id));
            if (res) {
                setBranches(res?.data);
            }
        } catch (error) {
            console.error(error);
        }
    }

    useQuery({ queryKey: ['getBranchesByCompany'], queryFn: getBranchesByCompany })

    return (
        <div className="py-6 px-3 md:px-6 w-full">
            <Formik
                initialValues={{
                    branchId: 0,
                    month: '',
                    targetAmount: 0,
                    year: 0,
                }}
                enableReinitialize={true}
                validateOnChange={true}
                validateOnBlur={true}
                validationSchema={Yup.object().shape({
                    month: Yup.string().trim().required('Month is required.'),
                    targetAmount: Yup.number()
                        .required('Target Amount is required.')
                        .positive('Target Amount must be a positive number.')
                        .min(1000, 'Target Amount must be greater than or equal to 1000.'),
                })}

                onSubmit={async (values: ICreateRevenueTargetDTO, { resetForm }) => {
                    const year = new Date(values.month).getFullYear()
                    const monthName = new Date(values.month).toLocaleString('default', { month: 'long' }).toUpperCase()

                    const payload: ICreateRevenueTargetDTO = {
                        branchId: Number(values.branchId),
                        month: monthName,
                        targetAmount: values.targetAmount,
                        year: year,
                    };

                    try {
                        const res: any = await setRevenueTarget(companyId, payload)

                        if (res) {
                            showToast.success(res?.message ?? 'Revenue target created successfully');
                            resetForm();
                            props.onProceed();
                        }
                    } catch (error: any) {
                        showToast.error(error?.description);
                    } finally {
                        setSubmitting(false);
                    }
                }}>
                {({ values, errors, isSubmitting, handleSubmit, handleChange, handleBlur, setFieldValue, touched }) => (
                    <form className="" onSubmit={handleSubmit}>
                        <div className="space-y-5">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Monthly Target</h3>
                            <SelectDropdown
                                name="branchId"
                                listSelectedValue={values.branchId}
                                onChange={(value: string) => {
                                    setFieldValue('branchId', value);
                                }}
                                label="Branch"
                                placeholder="Select branch"
                                list={branches?.map((branch: any) => ({
                                    id: branch.id,
                                    name: branch.name,
                                    value: branch.id
                                }))}
                                required
                                error={touched.branchId ? errors.branchId : undefined}
                                helperText={touched?.branchId ? errors.branchId : undefined}
                                selectInputClassName="rounded"
                            />


                            <Input
                                name="targetAmount"
                                value={values.targetAmount}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                label="Target Amount"
                                placeholder="Enter target amount"
                                required
                                error={touched.targetAmount ? errors.targetAmount : undefined}
                                helperText={touched?.targetAmount ? errors.targetAmount : undefined}
                            />

                            <DatePickerInput
                                id="month"
                                selected={values.month ? new Date(values.month) : null}
                                onChange={(date: Date | null) => {
                                    setFieldValue('month', date)
                                }}
                                dateFormat="MM-dd-yyyy"
                                classes="w-full min-w-screen"
                                label="Month - Year"
                                placeholder="Select month and year"
                                showMonthDropdown
                                showMonthsOnly
                                allowEarlierSelections={false}
                            />
                        </div>

                        <div className="w-full flex gap-4 mt-6 !mb-4">
                            <Button
                                type="button"
                                label="Cancel"
                                onClick={props.onClose}
                                bgColor='secondary'
                  fullWidth
                                loading={submitting}
                                disabled={submitting}
                            />
                            <Button
                                type="submit"
                                label="Set Target"
                                bgColor='primary'
              fullWidth
                                loading={submitting}
                                disabled={submitting}
                            />
                        </div>
                    </form>
                )}
            </Formik>
        </div>
    );
};

export default CreateRevenueTarget;
